/**
 * Shared mandate definitions used by:
 *   - /strategies (Strategies index page)
 *   - /strategies/:slug (individual mandate detail page)
 *   - homepage Strategies module
 *
 * Slugs MUST remain stable — they form public URLs.
 */
export const STRATEGIES = [
  {
    n: "01",
    slug: "industrial-credit",
    title: "Industrial Credit",
    summary:
      "Senior-secured and unitranche financings to lower- and mid-market industrial operators with tangible collateral and cash-flow durability.",
    thesis:
      "Bank retrenchment from the lower- and mid-market industrial complex has opened a structural opportunity for disciplined, asset-backed senior-secured capital. The platform underwrites against tangible collateral, contracted utilisation and recurring revenue characteristics — and refuses to commit where collateral, contracts or control rights do not compound.",
    approach: [
      "Asset-backed senior secured loans against plant, equipment, real estate, receivables and inventory.",
      "Unitranche and stretched-senior structures with intercreditor and security packages.",
      "Covenant-led structures preferring contracted utilisation and recurring revenue characteristics.",
      "Equity-linked enhancements where appropriate to the credit profile.",
      "Engagement with industrial sponsors and operating counterparties on a documented basis.",
    ],
    sectors: [
      "Industrial manufacturing & precision engineering",
      "Logistics, ports & operationally critical real estate",
      "Energy transition & onshoring infrastructure",
      "Specialty chemicals & process industries",
    ],
    deal: {
      size: "USD 25m – 200m+",
      tenor: "3 – 7 years",
      seniority: "Senior secured · unitranche",
      coverage: "Asset, cash-flow & contract coverage",
    },
    vehicles: [
      "KR Credit Opportunities Fund",
      "KR Real Estate Credit Fund I",
      "KR Debt Solutions",
    ],
  },
  {
    n: "02",
    slug: "bridge-capital",
    title: "Bridge Capital & Special Situations",
    summary:
      "Time-sensitive financings ahead of refinancing, capital events or recapitalisations — structured with covenants, collateral and a defined exit.",
    thesis:
      "A bridge is not a thesis — it is a written, time-bound path from one capital structure to the next. Where the exit is documented and supported by tangible asset coverage and contracted revenue, the partnership commits. Where the path is uncertain, it declines.",
    approach: [
      "Pre-event bridge financings written to a documented refinancing or capital event.",
      "Rescue capital and recapitalisation solutions for operators with tangible asset bases.",
      "Covenant and security packages calibrated to the exit, not the promise.",
      "Selective programmatic engagement with sponsors and operating counterparties.",
      "Defined-tenor capital — typically 12 to 36 months — with documented path to refinancing.",
    ],
    sectors: [
      "Sponsor-backed industrials approaching capital events",
      "Operators awaiting strategic or M&A-driven refinancing",
      "Rescue capital across asset-rich operating businesses",
      "Refinancing of legacy bank facilities",
    ],
    deal: {
      size: "USD 15m – 150m+",
      tenor: "12 – 36 months",
      seniority: "Senior secured · structured",
      coverage: "Path-to-exit underwritten, not assumed",
    },
    vehicles: ["KR Special Situations Fund I", "KR Co-Investment Vehicles"],
  },
  {
    n: "03",
    slug: "opportunistic-equity",
    title: "Opportunistic Industrial Equity",
    summary:
      "Control and influence equity in asset-rich industrial businesses where tangible assets and contracted revenues support an institutional value-realisation thesis.",
    thesis:
      "Opportunistic equity is underwritten — never narrated. The partnership commits to control or influence positions in industrial businesses where asset coverage, contracted revenue and operating leverage support a documented value-realisation pathway. The discipline is selective; the engagement is institutional.",
    approach: [
      "Control or influence positions with documented governance and exit alignment.",
      "Asset coverage, contracted revenue and operating leverage as the underwriting spine.",
      "Engagement with the operating partner network across diligence and value creation.",
      "Selective, sponsor-aligned execution — neither volume nor velocity led.",
      "Documented value-creation roadmap with quarterly governance and reporting cadence.",
    ],
    sectors: [
      "Asset-heavy industrial businesses with operational levers",
      "Carve-outs from larger industrial groups",
      "Family-owned industrial succession opportunities",
      "Mid-cycle industrial businesses requiring institutional capital",
    ],
    deal: {
      size: "USD 30m – 250m+ equity",
      tenor: "4 – 7 year hold",
      seniority: "Control · influence · co-control",
      coverage: "Operational, governance & exit alignment",
    },
    vehicles: [
      "KR Strategic Equity Fund I",
      "KR Real Assets Fund I",
      "KR Co-Investment Vehicles",
    ],
  },
  {
    n: "04",
    slug: "structured-solutions",
    title: "Structured Capital Solutions",
    summary:
      "Bespoke preferred, hybrid and structured instruments — written with a preference for downside protection and risk-adjusted outcomes across the capital stack.",
    thesis:
      "Structured capital sits between credit and equity for a reason: it is engineered around the counterparty's needs and the partnership's underwriting standards. Preferred, hybrid and structured instruments are documented with protections that compound capital preservation — across operating outcomes and capital cycles.",
    approach: [
      "Preferred equity, structured equity and convertible instruments with documented protections.",
      "Hybrid structures spanning credit / equity boundaries calibrated to capital needs.",
      "Programmatic SMAs, warehouse facilities and forward-flow arrangements with institutional counterparties.",
      "Long-duration infrastructure exposure across energy transition, logistics and onshoring.",
      "Documented intercreditor, security and governance packages with downside-first calibration.",
    ],
    sectors: [
      "Long-duration industrial infrastructure",
      "Capital-intensive operators requiring structured support",
      "Forward-flow & warehouse relationships with originators",
      "Bespoke single-investor mandates",
    ],
    deal: {
      size: "USD 20m – 200m+",
      tenor: "5 – 10 years",
      seniority: "Preferred · hybrid · structured",
      coverage: "Engineered, not narrated",
    },
    vehicles: [
      "KR Infrastructure Fund I",
      "KR Strategic Accounts",
      "KR Debt Solutions",
    ],
  },
];

export const STRATEGY_BY_SLUG = Object.fromEntries(
  STRATEGIES.map((s) => [s.slug, s])
);
